import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { directionsApi, type ItineraryRouteDay, type TransferKind } from '../api/directions.api';
import { transferKindOf } from '../utils/legFormatting';
import type { ItineraryDay } from '../types';

/**
 * Travel times between the stops of the itinerary, in two steps (ADR-0011).
 *
 * The itinerary is the tab every trip opens on, and its legs multiply with the
 * number of days the trip has — a 10-day trip with 6 stops a day is ~50 legs at
 * €0,005 each. Resolving them all on mount would put that on the bill of every
 * trip anyone opens, for days most people never look at. So:
 *
 * 1. On mount, ask for what the server already has cached. That request cannot
 *    bill (`cachedOnly` never reaches the Routes call), so it is the same
 *    free-on-mount exception `hasCachedDetails` uses for venue details. In
 *    practice most legs are already there: the cache is global by coordinate
 *    pair, so anyone who opened the map has paid for them.
 * 2. What is still missing after that is bought **for the day the user has
 *    open, and for no other** — ADR-0011. Opening a day is the gesture; asking
 *    for a second click on top of it bought nothing. `resolveDay` stays exported
 *    because the day-by-day bound is the decision, not the button.
 *
 * Times are keyed by the stop they LEAVE, not by leg index: the server routes
 * only the stops it can place (`isRoutable`), so its leg list and the cards on
 * screen are not the same sequence and an index would line them up wrong.
 */

interface DayRoute {
  /** How the server ordered the stops it could route. */
  activityIds: string[];
  /** Seconds for the leg leaving each stop; null where it has no answer yet. */
  legs: (number | null)[];
  /** ADR-0031: the legs that are a flight or a transfer, by the stop they leave. */
  transfers: Record<string, TransferKind>;
}

export interface DayLegs {
  /** Seconds of travel from this stop to the next, or undefined if unknown — or a transfer. */
  secondsAfter: (activityId: string) => number | undefined;
  /** ADR-0031: the leg leaving this stop is a flight or a transfer, with no time to show. */
  transferAfter: (activityId: string) => TransferKind | undefined;
  /** Legs of this day with no answer yet. Includes the ones there is no answer FOR. */
  missingCount: (dayId: string) => number;
  /**
   * Whether this day has already been through a paid resolve. After that, a leg
   * still missing is a leg with **no route** — an island to a mainland, say —
   * not one waiting to be bought. Asking again would spend nothing and find
   * nothing, so nothing should offer to.
   */
  attempted: (dayId: string) => boolean;
  /** The day currently being resolved, if any. */
  resolving: string | null;
  resolveDay: (dayId: string) => Promise<void>;
}

function toRoutes(days: ItineraryRouteDay[]): Record<string, DayRoute> {
  return Object.fromEntries(days.map(d => [
    d.dayId,
    {
      activityIds: d.activityIds,
      legs: d.legs.map(l => l?.durationSec ?? null),
      transfers: Object.fromEntries(d.legs.flatMap((l, i) => {
        const kind = transferKindOf(l);
        return kind ? [[d.activityIds[i], kind]] : [];
      })),
    },
  ]));
}

export function useDayLegs(
  tripId: string,
  days: ItineraryDay[],
  /** The day on screen. Its missing legs get bought; nobody else's do. */
  openDayId?: string | null,
  /**
   * Called after a day's legs land. New travel is new input to that day's
   * clock (ADR-0010) and the server has just recomputed it, so the caller has
   * to pick the day back up or it keeps painting the hours from before.
   */
  onDayResolved?: (dayId: string) => void,
): DayLegs {
  const [routes, setRoutes] = useState<Record<string, DayRoute>>({});
  const [resolving, setResolving] = useState<string | null>(null);
  // ADR-0011: one purchase per day, ever. Without this the effect below would
  // re-buy on every render where something is still unroutable — the server
  // answers null for a stop with no coordinates, so `missingCount` never
  // reaches zero for those and the loop would be silent and expensive.
  const bought = useRef<Set<string>>(new Set());

  // Keyed on the stop order rather than on `days`: the parent rebuilds that
  // array on every render, and re-asking on each one would be a request per
  // keystroke. A reorder or a cross-day move genuinely changes which pairs are
  // needed, and re-reading is free — which is the point of the cached pass.
  const shape = days.map(d => `${d.id}:${d.activities.map(a => a.id).join(',')}`).join('|');

  useEffect(() => {
    if (days.length === 0) return;
    let alive = true;
    directionsApi.route(tripId, { cachedOnly: true })
      .then(routeDays => { if (alive) setRoutes(toRoutes(routeDays)); })
      // Nothing cached yet is a normal state, not an error: the day simply shows
      // no times and offers the button.
      .catch(() => { /* silent by design */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, shape]);

  const byActivity = useMemo(() => {
    const out: Record<string, number> = {};
    for (const route of Object.values(routes)) {
      route.legs.forEach((secs, i) => {
        const from = route.activityIds[i];
        if (secs !== null && !route.transfers[from]) out[from] = secs;
      });
    }
    return out;
  }, [routes]);
  const transfersByActivity = useMemo(
    () => Object.assign({}, ...Object.values(routes).map(r => r.transfers)) as Record<string, TransferKind>,
    [routes],
  );

  const secondsAfter = useCallback((activityId: string) => byActivity[activityId], [byActivity]);
  const transferAfter = useCallback((activityId: string) => transfersByActivity[activityId], [transfersByActivity]);

  const missingCount = useCallback(
    (dayId: string) => (routes[dayId]?.legs ?? []).filter(l => l === null).length,
    [routes],
  );

  // `bought` is a ref, so this needs `routes` to re-run: the resolve that fills
  // it also lands new routes, which is the render where callers re-read this.
  const attempted = useCallback(
    (dayId: string) => bought.current.has(dayId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routes],
  );

  /** The paid half: one day, because that is the day the user has open. */
  const resolveDay = useCallback(async (dayId: string) => {
    if (resolving) return;
    bought.current.add(dayId);
    setResolving(dayId);
    try {
      const routeDays = await directionsApi.route(tripId, { dayId });
      setRoutes(prev => ({ ...prev, ...toRoutes(routeDays) }));
      onDayResolved?.(dayId);
    } catch {
      /* the day simply keeps showing the times it already had */
    } finally {
      setResolving(null);
    }
  }, [tripId, resolving, onDayResolved]);

  /**
   * ADR-0011: the open day, and only the open day.
   *
   * Three guards, and each one is load-bearing. `routes[openDayId]` means the
   * free cached pass has already answered for this day — without it the first
   * render would buy legs the cache was about to hand over. `bought` means once
   * per day per session: a stop with no coordinates comes back null forever, so
   * `missingCount` would never reach zero and this would loop, quietly, at
   * €0,005 a turn. And `resolving` keeps two days from overlapping.
   */
  useEffect(() => {
    if (!openDayId || resolving) return;
    if (bought.current.has(openDayId)) return;
    if (!routes[openDayId]) return;
    if (missingCount(openDayId) === 0) return;
    void resolveDay(openDayId);
  }, [openDayId, routes, resolving, missingCount, resolveDay]);

  return { secondsAfter, transferAfter, missingCount, attempted, resolving, resolveDay };
}
